import { AudioConfig } from '../types/game.types';

export class AudioManager {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private config: AudioConfig;
  private isInitialized: boolean = false;

  constructor() {
    this.config = {
      volume: 0.7,
      muted: false
    };
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create master gain node
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = this.config.volume;

      this.isInitialized = true;
      console.log('AudioManager initialized');
    } catch (error) {
      console.error('Failed to initialize AudioManager:', error);
    }
  }

  public async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // Simple tone generation (C64 SID-style)
  public playTone(frequency: number, duration: number, waveform: OscillatorType = 'square'): void {
    if (!this.audioContext || !this.masterGain || this.config.muted) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Set waveform and frequency
    oscillator.type = waveform;
    oscillator.frequency.value = frequency;

    // Envelope (quick attack, exponential decay)
    const now = this.audioContext.currentTime;
    gainNode.gain.value = 0;
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Attack
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration); // Decay

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  // Common game sound effects
  public playShoot(): void {
    this.playTone(800, 0.1, 'square');
  }

  public playExplosion(): void {
    if (!this.audioContext || !this.masterGain || this.config.muted) return;

    const now = this.audioContext.currentTime;
    
    // Create noise-like explosion sound
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Low-pass filtered sawtooth for explosion
    oscillator.type = 'sawtooth';
    oscillator.frequency.value = 100;
    
    filter.type = 'lowpass';
    filter.frequency.value = 200;

    // Envelope
    gainNode.gain.value = 0;
    gainNode.gain.linearRampToValueAtTime(0.5, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    oscillator.start(now);
    oscillator.stop(now + 0.3);
  }

  public playPowerUp(): void {
    if (!this.audioContext || !this.masterGain || this.config.muted) return;

    const now = this.audioContext.currentTime;
    
    // Ascending tone sequence
    const frequencies = [220, 277, 330, 440];
    const duration = 0.1;

    frequencies.forEach((freq, index) => {
      const startTime = now + index * duration;
      
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.masterGain!);

      oscillator.type = 'triangle';
      oscillator.frequency.value = freq;

      gainNode.gain.value = 0;
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
  }

  public playGameOver(): void {
    if (!this.audioContext || !this.masterGain || this.config.muted) return;

    const now = this.audioContext.currentTime;
    
    // Descending tone sequence
    const frequencies = [440, 370, 311, 220];
    const duration = 0.2;

    frequencies.forEach((freq, index) => {
      const startTime = now + index * duration;
      
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.masterGain!);

      oscillator.type = 'triangle';
      oscillator.frequency.value = freq;

      gainNode.gain.value = 0;
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
  }

  public playMenuMove(): void {
    this.playTone(440, 0.05, 'square');
  }

  public playMenuSelect(): void {
    this.playTone(660, 0.1, 'square');
  }

  // Volume and mute controls
  public setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.config.muted ? 0 : this.config.volume;
    }
  }

  public getVolume(): number {
    return this.config.volume;
  }

  public mute(): void {
    this.config.muted = true;
    if (this.masterGain) {
      this.masterGain.gain.value = 0;
    }
  }

  public unmute(): void {
    this.config.muted = false;
    if (this.masterGain) {
      this.masterGain.gain.value = this.config.volume;
    }
  }

  public toggleMute(): void {
    if (this.config.muted) {
      this.unmute();
    } else {
      this.mute();
    }
    
    console.log(`Audio ${this.config.muted ? 'muted' : 'unmuted'}`);
  }

  public isMuted(): boolean {
    return this.config.muted;
  }

  // Simple background music (C64-style short loops)
  public startBackgroundMusic(): void {
    if (!this.audioContext || !this.masterGain || this.config.muted) return;

    // Simple C64-style melody loop
    const melody = [
      { freq: 262, dur: 0.25 }, // C
      { freq: 330, dur: 0.25 }, // E
      { freq: 392, dur: 0.25 }, // G
      { freq: 523, dur: 0.25 }, // C
      { freq: 392, dur: 0.25 }, // G
      { freq: 330, dur: 0.25 }, // E
      { freq: 262, dur: 0.5 }   // C
    ];

    let currentTime = this.audioContext.currentTime;
    
    melody.forEach(note => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.masterGain!);

      oscillator.type = 'triangle';
      oscillator.frequency.value = note.freq;

      gainNode.gain.value = 0;
      gainNode.gain.linearRampToValueAtTime(0.1, currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + note.dur);

      oscillator.start(currentTime);
      oscillator.stop(currentTime + note.dur);

      currentTime += note.dur;
    });
  }

  public cleanup(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.masterGain = null;
      this.isInitialized = false;
    }
  }
}